import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { MqttMessage, SubscriptionConfig } from '../types';
import { cleanupAtomFamilies } from '../utils/mqttClientOperations';

export const topicDataAtomFamily = atomFamily((topic: string) => {
  const dataAtom = atom<MqttMessage | null>(null);
  dataAtom.debugLabel = `topicData:${topic}`;
  return dataAtom;
});

export const subscriberSetAtomFamily = atomFamily((topic: string) => {
  const subscriberAtom = atom<Set<string>>(new Set<string>());
  subscriberAtom.debugLabel = `subscribers:${topic}`;
  return subscriberAtom;
});

export const subscriptionConfigAtomFamily = atomFamily((topic: string) => {
  const configAtom = atom<SubscriptionConfig | undefined>(undefined);
  configAtom.debugLabel = `config:${topic}`;
  return configAtom;
});

export const activeTopicsAtom = atom<Set<string>>(new Set<string>());

export const addSubscriberAtom = atom(
  null,
  (
    get,
    set,
    { topic, subscriberId }: { topic: string; subscriberId: string },
  ) => {
    const currentSubscribers = get(subscriberSetAtomFamily(topic));
    const newSubscribers = new Set(currentSubscribers);
    newSubscribers.add(subscriberId);
    set(subscriberSetAtomFamily(topic), newSubscribers);

    const activeTopics = get(activeTopicsAtom);
    if (!activeTopics.has(topic)) {
      const newActiveTopics = new Set(activeTopics);
      newActiveTopics.add(topic);
      set(activeTopicsAtom, newActiveTopics);
    }
  },
);

export const removeSubscriberAtom = atom(
  null,
  (
    get,
    set,
    { topic, subscriberId }: { topic: string; subscriberId: string },
  ) => {
    const currentSubscribers = get(subscriberSetAtomFamily(topic));
    const newSubscribers = new Set(currentSubscribers);
    newSubscribers.delete(subscriberId);
    set(subscriberSetAtomFamily(topic), newSubscribers);

    if (newSubscribers.size === 0) {
      const activeTopics = get(activeTopicsAtom);
      const newActiveTopics = new Set(activeTopics);
      newActiveTopics.delete(topic);
      set(activeTopicsAtom, newActiveTopics);

      set(topicDataAtomFamily(topic), null);
      set(subscriptionConfigAtomFamily(topic), undefined);

      cleanupAtomFamilies(
        topic,
        topicDataAtomFamily,
        subscriberSetAtomFamily,
        subscriptionConfigAtomFamily,
      );
    }
  },
);

export const updateTopicDataAtom = atom(
  null,
  (_get, set, { topic, message }: { topic: string; message: MqttMessage }) => {
    set(topicDataAtomFamily(topic), message);
  },
);

import { setDebugLabels } from '../utils/atomUtils';

setDebugLabels({
  activeTopics: activeTopicsAtom,
  addSubscriberAtom,
  removeSubscriberAtom,
  updateTopicDataAtom,
});
